import { Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { BriefingService } from '../services';
import { BehaviorSubject, Subject } from 'rxjs';
import * as _ from 'lodash';
import { flatMap, takeUntil, filter } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { BriefingHistoryDetailsDialogComponent } from './briefingHistoryDetailsDialog.component';

@Component({
    selector: 'briefing-history',
    styles: [`
        .cdk-column-itemsSent.mat-column-itemsSent {
            text-align: center;
        }
        .cdk-column-sent.mat-column-sent {
            width: 150;
        }
        .cdk-column-recipients.mat-column-recipients {
            width: 225px;
        }
    `],
    template: `
            <div class="scroll-container" fxFlex infiniteScroll [infiniteScrollThrottle]="300" [scrollWindow]="false" (scrolled)="nextPage()">
                <table mat-table *ngIf="history" #table multiTemplateDataRows [dataSource]="history" fxFlex width="100%;">
                    <ng-container matColumnDef="subject">
                        <th mat-header-cell *matHeaderCellDef>Subject</th>
                        <td mat-cell *matCellDef="let row">
                            <a target="_blank" [href]="revLink(row)">{{row.subject}}</a>
                        </td>
                    </ng-container>    
                    <ng-container matColumnDef="sent">
                        <th mat-header-cell *matHeaderCellDef>Sent</th>
                        <td mat-cell *matCellDef="let row">{{row.created | date: 'short'}}</td>
                    </ng-container>
                    <ng-container matColumnDef="sendType">
                        <th mat-header-cell *matHeaderCellDef>Send Type</th>
                        <td mat-cell *matCellDef="let row">{{ statusLabel(row.type) }}</td>
                    </ng-container>	
                    <ng-container matColumnDef="itemsSent">
                        <th mat-header-cell *matHeaderCellDef>New Items</th>
                        <td mat-cell *matCellDef="let row">{{row.numberOfItemsSent }}</td>
                    </ng-container>
                    <ng-container matColumnDef="note">
                        <th mat-header-cell *matHeaderCellDef>Note</th>
                        <td mat-cell *matCellDef="let row">{{ historyNote(row) }}</td>
                    </ng-container>
                    <ng-container matColumnDef="recipients">
                        <th mat-header-cell *matHeaderCellDef>Recipients</th>
                        <td mat-cell *matCellDef="let row">
                            <div style="padding-top: 3px; padding-bottom: 3px;">
                                <mat-chip-list>
                                    <mat-chip><a (click)="historyRecipientDetails(row)">Recipients ({{row.recipients?.length || 0}})</a></mat-chip>
                                    <mat-chip><a (click)="historyDetails(row, 'delivered')">Delivered ({{rowStats(row, 'delivered')}})</a></mat-chip>
                                    <mat-chip><a (click)="historyDetails(row, 'opened')">Opened ({{rowStats(row, 'opened')}})</a></mat-chip>
                                    <mat-chip><a (click)="historyDetails(row, 'clicked')">Clicked ({{rowStats(row, 'clicked')}})</a></mat-chip>
                                    <mat-chip><a (click)="historyDetails(row, 'bounced')">Bounced ({{rowStats(row, 'bounced')}})</a></mat-chip>
                                    <mat-chip><a (click)="historyDetails(row, 'dropped')">Dropped ({{rowStats(row, 'dropped')}})</a></mat-chip>
                                </mat-chip-list>
                            </div>
                        </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="columns"></tr>
                    <tr mat-row *matRowDef="let row; columns: columns;"></tr>
                </table>
                <div *ngIf="noTouchingPageFetch" fxLayout="column" fxLayoutAlign="center center">
                    <spinner></spinner>
                </div>
            </div>
    `
})
export class BriefingHistoryComponent implements OnInit, OnDestroy, OnChanges {
    @Input() briefingId: string;
    @Input() type: string;
    private readonly slackAndTeamsColumns = ['sent', 'sendType', 'itemsSent', 'note'];
    private readonly emailColumns = ['subject', 'sent', 'sendType', 'itemsSent', 'note', 'recipients']; 
    columns = [];
    private cleanupTime = new Subject<void>();
    private params = new BehaviorSubject<any>(null);
    history = new BehaviorSubject<any[]>([]);
    noTouchingPageFetch: boolean;
    lastFetched: boolean;
    private readonly noteMap = {
        'briefing sent successfully.': 'Successfully sent',
        'briefing had no new items.': 'No new items',
        'briefing has no subscribers.': 'No subscribers'
    }

    constructor(private dialog: MatDialog, private briefingService: BriefingService) {
        this.params
        .pipe(
            filter(() => !!this.briefingId),
            flatMap(params => this.briefingService.getHistory(this.briefingId, params)),
            takeUntil(this.cleanupTime)
        )
        .subscribe(history => {
            this.lastFetched = history.last;
            this.history.next(this.history.value.concat(history.content));
        });

        briefingService.briefingHistoryChanged
        .pipe(
            filter(briefingId => briefingId === this.briefingId),
            takeUntil(this.cleanupTime)
        )
        .subscribe(() => {
            this.history.next([]);
            this.params.next({ page: 0, rows: 50 });
        });
    }

    ngOnInit(): void {
        this.params.next({ page: 0, rows: 50 });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.type) {
            this.columns = changes.type.currentValue  === 'SLACK' || changes.type.currentValue === 'TEAMS' ? this.slackAndTeamsColumns : this.emailColumns;
        }
    }

    statusLabel(status): string {
        return _.startCase((status || '').toLowerCase())
    }

    historyNote(history): string {
        const note = this.noteMap[history.note.toLowerCase()] || history.note;
        return history.sent ? 'Successfully sent' : note;
    }

    nextPage(): void {
        const params = this.params.value;
        params.page = params.page + 1;
        this.params.next(params);
    }

    rowStats(row, key): number {
        const actSet = new Set(row.activities.filter(act => act.activity.endsWith(key)).map(act => act.emailAddress));
        return actSet.size
    }

    historyRecipientDetails(history) {
        this.dialog.open(BriefingHistoryDetailsDialogComponent, { data: { history: { activities: history.recipients.map(recipient => ({ emailAddress: recipient })) } } });
    }

    historyDetails(history, filter: string) {
        this.dialog.open(BriefingHistoryDetailsDialogComponent, { data: { history, filter } });
    }

    revLink(history): string {
        return `${window.location.origin}/briefing?id=${history.briefingId}&briefingHistoryId=${history.id}`;
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
    }
}